/**
 * Verbal chart palette — monochrome with semantic accents.
 *
 * Use the ink-based scale for neutral data series; use semantic colors
 * (salmon, gain, burn) only when the data has a specific valence.
 *
 * For provider/model color mapping, see chart-colors.ts.
 */

export const chartPalette = {
  // Primary series — ink at varying opacities
  primary: 'var(--color-ink)',
  secondary: 'rgba(26, 25, 22, 0.6)',
  tertiary: 'rgba(26, 25, 22, 0.3)',

  // Semantic (use sparingly — reserved for insight moments)
  accent: 'var(--color-salmon)',
  gain: 'var(--color-gain)',
  burn: 'var(--color-burn)',

  // UI chrome
  grid: 'rgba(229, 225, 219, 0.6)',   // --color-rule at 60%
  axis: 'var(--color-graphite)',
  background: 'var(--color-paper)',
  surface: 'var(--color-vellum)',
} as const

// Multi-series palette for categorical data.
// Order: insight-accent first, then monochrome descending, semantic last.
// This ordering keeps the accent (coral) and semantic colors (gain/burn)
// separated by neutral ink steps so no two adjacent series ever land on the
// red/green pair that's worst-case for colorblind viewers.
// Extend with more ink lightness steps (not new hues) if a chart needs more
// than 6 distinct series — see the @90%/@80% steps below. Steps are capped
// at ink@60% (secondary) on the low end: anything lighter drops under the
// WCAG 1.4.11 non-text 3:1 contrast floor against --color-paper, which is
// why chartPalette.tertiary (ink@30%, ~1.9:1) is used sparingly upstream and
// not extended further here.
export const chartSeries = [
  chartPalette.accent,
  chartPalette.primary,
  chartPalette.secondary,
  chartPalette.tertiary,
  chartPalette.gain,
  chartPalette.burn,
  'rgba(26, 25, 22, 0.9)',  // ink @90% (~12.5:1) — extra step for >6-series charts
  'rgba(26, 25, 22, 0.8)',  // ink @80% (~8.9:1) — extra step for >7-series charts
] as const

// Default props for Recharts components.
// Spread these onto the matching Recharts element to apply the FT aesthetic.
export const chartDefaults = {
  cartesianGrid: {
    stroke: chartPalette.grid,
    strokeDasharray: '0',   // solid lines, NOT dashed
    vertical: false,         // horizontal gridlines only
  },
  xAxis: {
    stroke: chartPalette.axis,
    fontSize: 11,
    fontFamily: 'var(--font-mono)',
    tickLine: false,
    axisLine: false,
  },
  yAxis: {
    stroke: chartPalette.axis,
    fontSize: 11,
    fontFamily: 'var(--font-mono)',
    tickLine: false,
    axisLine: false,
  },
  tooltip: {
    contentStyle: {
      backgroundColor: 'var(--color-paper)',
      border: '1px solid var(--color-rule)',
      borderRadius: '2px',
      fontFamily: 'var(--font-sans)',
      fontSize: '12px',
      color: 'var(--color-ink)',
      boxShadow: '0 1px 3px rgba(26, 25, 22, 0.08)',
    },
    cursor: { fill: 'var(--color-vellum)', opacity: 0.5 },
  },
} as const
